import { Device } from '@capacitor/device';
import { UAParser } from 'ua-parser-js';
import { breakpoints } from 'web-app/styleguide/utils';
import LocalStorageService from 'web-app/services/local-storage';
import SHARED_ENVIRONMENT_CONFIG from 'web-app/shared/environment';
import { customAtLeastOneLargerThanLandscape } from 'web-app/styleguide/breakpoints';
import { hasValue } from '@famly/stat_ts-utils_has-value';
class PlatformHelper {
    static hasTouchCapability() {
        // Check the actual hardware touch points - most reliable indicator
        if (navigator.maxTouchPoints === 0) {
            return false;
        }
        // Additional check using the pointer media query
        // This is more reliable than the 'ontouchstart' check
        if (window.matchMedia('(pointer: coarse)').matches === false &&
            window.matchMedia('(any-pointer: coarse)').matches === false) {
            return false; // Device doesn't have touch as primary or secondary input
        }
        return true;
    }
    static isDebug() {
        return Boolean(LocalStorageService.getFromLocalStorage('debugMode'));
    }
    static isAndroid() {
        return SHARED_ENVIRONMENT_CONFIG.CURRENT_PLATFORM === 'android';
    }
    static isIos() {
        return SHARED_ENVIRONMENT_CONFIG.CURRENT_PLATFORM === 'ios';
    }
    static isMobileApp() {
        return this.isAndroid() || this.isIos();
    }
    static isWeb() {
        return (SHARED_ENVIRONMENT_CONFIG.CURRENT_PLATFORM === 'html' ||
            SHARED_ENVIRONMENT_CONFIG.CURRENT_PLATFORM === 'docker');
    }
    static shouldUseNativeInputFields() {
        return this.isMobileApp();
    }
    // https://stackoverflow.com/questions/3007480/determine-if-user-navigated-from-mobile-safari
    static isSafariMobile() {
        const ua = window.navigator.userAgent;
        const iOS = Boolean(ua.match(/iPad/i)) || Boolean(ua.match(/iPhone/i));
        const webkit = Boolean(ua.match(/WebKit/i));
        const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
        return iOSSafari;
    }
    static isMobileSize() {
        return this.matchMediaMaxWidth(breakpoints.mobile);
    }
    static isMobile() {
        return this.isMobileApp() || this.isMobileSize();
    }
    // For non-native mobile apps, in our case the sign in app: returns true if the device is a tablet (even if running in its browser)
    static isRunningOnTablet() {
        const uaParser = new UAParser();
        return uaParser.getDevice().type === 'tablet';
    }
    // For non-native mobile apps, in our case the sign in app: returns true if the device is running on iOS
    static isRunningOnIOS() {
        const uaParser = new UAParser();
        return uaParser.getOS().name === 'iOS';
    }
    /**
     * Returns true if the browser is webkit based _and_ is not the mobile app
     *
     * @returns boolean
     */
    static isWebkitWeb() {
        const ua = window.navigator.userAgent;
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#making_the_best_of_user_agent_sniffing
        const isWebkit = /\b(iPad|iPhone|iPod)\b/.test(ua) && /WebKit/.test(ua);
        return !this.isMobileApp() && isWebkit;
    }
    static isTabletSize() {
        return this.matchMediaMaxWidth(breakpoints.tablet);
    }
    static isTablet() {
        return this.isMobileApp() || this.isTabletSize();
    }
    static isLandscapeSize() {
        return this.matchMediaMaxWidth(breakpoints.landscape);
    }
    static atLeastLandscape() {
        return this.matchMediaMinWidth(breakpoints.landscape);
    }
    static matchMediaMinWidth(breakpoint) {
        return window.matchMedia(`(min-width: ${breakpoint})`).matches;
    }
    static matchMediaMaxWidth(breakpoint) {
        return window.matchMedia(`(max-width: ${breakpoint})`).matches;
    }
    static isPortraitSize() {
        return window.matchMedia(`(max-width: ${breakpoints.portrait})`).matches;
    }
    static isDesktopSize() {
        return !(this.isMobileSize() || this.isTabletSize());
    }
    static isTouch() {
        return 'ontouchstart' in window || this.isMobileApp();
    }
    /**
     * New breakpoints
     */
    static customAtLeastOneLargerThanLandscape() {
        return window.matchMedia(customAtLeastOneLargerThanLandscape).matches;
    }
    static hasPdfInIframeSupport() {
        return !this.isMobileApp();
    }
    static viewportSize() {
        /**
         * From https://stackoverflow.com/a/8876069
         */
        return {
            width: Math.max(document.documentElement.clientWidth, window.innerWidth || 0) || null,
            height: Math.max(document.documentElement.clientHeight, window.innerHeight || 0) || null,
        };
    }
    static store() {
        return SHARED_ENVIRONMENT_CONFIG.CURRENT_STORE && isStore(SHARED_ENVIRONMENT_CONFIG.CURRENT_STORE)
            ? SHARED_ENVIRONMENT_CONFIG.CURRENT_STORE
            : this.fallbackStore();
    }
    static allowPrint() {
        // Detection method from https://stackoverflow.com/a/29696509
        const ua = window.navigator.userAgent;
        const iOS = ua.match(/iPad/i) || ua.match(/iPhone/i);
        const webkit = Boolean(ua.match(/WebKit/i));
        const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
        return !iOSSafari && !this.isIos();
    }
    static version() {
        return `${SHARED_ENVIRONMENT_CONFIG.APP.version}${this.isMobileApp() ? ` (${SHARED_ENVIRONMENT_CONFIG.APP.buildNumber})` : ''}`;
    }
    /**
     * Hints at whether we should dynamically change styling of the status bar
     * At the moment only relevant for iOS as we only "extend" the app contents to the status bar area on that platform.
     */
    static hasDynamicStatusBar() {
        return this.isMobileApp();
    }
    static fallbackStore() {
        if (this.isIos()) {
            return 'APPSTORE';
        }
        else if (this.isAndroid()) {
            return 'PLAYSTORE';
        }
        else {
            return undefined;
        }
    }
    /*
     * Returns the device major version if the user is in the mobile app and we can access it, null otherwise.
     */
    static mobileAppDeviceMajorVersion() {
        if (!this.isMobileApp()) {
            return Promise.resolve(null);
        }
        // This is how you get the device version in Capacitor:
        // https://capacitorjs.com/docs/apis/device#getinfo
        return Device.getInfo().then(info => {
            // They don't document what happens if they can't get the version, but I decided
            // to include a fallback just in case
            const deviceMajorVersion = info.osVersion.split('.')[0] || null;
            return hasValue(deviceMajorVersion) ? Number(deviceMajorVersion) : null;
        });
    }
    static isIosVersionOrAbove(version) {
        return this.mobileAppDeviceMajorVersion().then(majorVersion => this.isIos() && hasValue(majorVersion) && majorVersion >= version);
    }
    static getManufacturer() {
        return Device.getInfo().then(info => info.manufacturer.toLowerCase());
    }
}
PlatformHelper.isSafariDesktop = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('safari') !== -1) {
        if (ua.indexOf('chrome') > -1) {
            return false;
        }
        else {
            return true;
        }
    }
    return false;
};
export default PlatformHelper;
const stores = ['APPSTORE', 'PLAYSTORE', 'AMAZONAPPSTORE'];
function isStore(candidate) {
    return stores.includes(candidate);
}
